<script setup lang="ts">
  import PosUiIcon from '@/components/pos-v2/ui/PosUiIcon.vue';
  import { ILanguage } from '@/interfaces/ILanguage';
  import { computed, getCurrentInstance } from 'vue';

  export interface ITranslationEntryModel {
    title: string | null;
    description: string | null;
    outdated?: boolean;
  }

  const props = withDefaults(
    defineProps<{
      language: ILanguage;
      /** Base-language text of the translated target (group or choice title). */
      label: string;
      value: ITranslationEntryModel;
      showDescription?: boolean;
    }>(),
    {
      showDescription: true,
    }
  );

  const emit = defineEmits<{
    (e: 'input', value: ITranslationEntryModel): void;
    (e: 'change'): void;
  }>();

  const proxy = getCurrentInstance()!.proxy;

  function i18nT(key: string, params?: Record<string, unknown>): string {
    return String(proxy.$t(key, params));
  }

  const isEmpty = computed(
    () => !props.value.title?.trim() && !props.value.description?.trim()
  );

  const titlePlaceholder = computed(() =>
    i18nT('translationRow.titlePlaceholder', { language: props.language.name })
  );

  const descriptionPlaceholder = computed(() =>
    i18nT('translationRow.descriptionPlaceholder', {
      language: props.language.name,
    })
  );

  function onTitleInput(ev: Event) {
    emit('input', {
      ...props.value,
      title: (ev.target as HTMLInputElement).value,
    });
    emit('change');
  }

  function onDescriptionInput(ev: Event) {
    emit('input', {
      ...props.value,
      description: (ev.target as HTMLTextAreaElement).value,
    });
    emit('change');
  }
</script>

<template>
  <div class="translation-entry-row">
    <div class="entry-header">
      <span class="entry-label">{{ label }}</span>
      <span v-if="value.outdated" class="entry-badge outdated">
        <PosUiIcon name="refresh" :size="14" class="badge-icon" />
        {{ $t('translationRow.outdated') }}
      </span>
      <span v-else-if="isEmpty" class="entry-badge pending">
        {{ $t('translationRow.pending') }}
      </span>
    </div>
    <div class="entry-fields">
      <input
        class="entry-title-input"
        type="text"
        maxlength="255"
        :value="value.title ?? ''"
        :placeholder="titlePlaceholder"
        @input="onTitleInput"
      />
      <textarea
        v-if="showDescription"
        class="entry-description-input"
        rows="2"
        :value="value.description ?? ''"
        :placeholder="descriptionPlaceholder"
        @input="onDescriptionInput"
      />
    </div>
  </div>
</template>

<style scoped lang="scss">
  .translation-entry-row {
    // Margins, not flex gap: old POS hardware (Chrome < 84).
    @apply tw-flex tw-flex-col tw-rounded-xl tw-border tw-border-neutral-200 tw-bg-surface tw-p-3;

    .entry-header {
      @apply tw-flex tw-items-center;

      .entry-label {
        @apply tw-text-sm tw-font-medium tw-text-neutral-900;
      }

      .entry-badge {
        @apply tw-ml-2 tw-inline-flex tw-items-center tw-rounded-full tw-px-2 tw-py-0.5 tw-text-xs;

        .badge-icon {
          @apply tw-mr-1;
        }

        &.outdated {
          @apply tw-bg-amber-50 tw-text-amber-700;
        }

        &.pending {
          @apply tw-bg-neutral-100 tw-text-neutral-500;
        }
      }
    }

    .entry-fields {
      @apply tw-mt-1.5 tw-flex tw-flex-col;

      .entry-title-input {
        @apply tw-h-11 tw-w-full tw-rounded-lg tw-border tw-border-solid tw-border-neutral-300 tw-bg-surface tw-px-3 tw-text-sm tw-text-neutral-700 tw-outline-none tw-transition-colors placeholder:tw-text-neutral-400;

        &:focus {
          @apply tw-border-orange-600 tw-ring-1 tw-ring-orange-600;
        }
      }

      .entry-description-input {
        @apply tw-mt-2 tw-w-full tw-resize-none tw-rounded-lg tw-border tw-border-solid tw-border-neutral-300 tw-bg-surface tw-px-3 tw-py-2 tw-text-sm tw-text-neutral-700 tw-outline-none tw-transition-colors placeholder:tw-text-neutral-400;

        &:focus {
          @apply tw-border-orange-600 tw-ring-1 tw-ring-orange-600;
        }
      }
    }
  }
</style>
